import {
  CircleCheck,
  CircleSlash,
  Flag,
  GitBranch,
  MessageCircleQuestion,
  MoreHorizontal,
  Play,
  Plus,
} from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { PlacedNode, PlacedPill } from "@/lib/flow-geometry";
import type { FlowNode } from "@/lib/intake-flow";
import { cn } from "@/lib/utils";

/**
 * What sits on the board (ACL-338).
 *
 * Every piece here takes its box as a prop and makes no layout decision of its own: the geometry
 * module owns where things go, so these stay dumb enough to be read at a glance and the positions
 * stay testable without a DOM.
 */

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  question: MessageCircleQuestion,
  paths: GitBranch,
  continue: CircleCheck,
  flag: Flag,
  reject: CircleSlash,
};

const BADGE: Record<string, string> = {
  question: "bg-accent-2 text-accent-11",
  paths: "bg-gray-3 text-text-muted",
  continue: "bg-success-2 text-success-11",
  flag: "bg-warning-2 text-warning-11",
  reject: "bg-danger-2 text-danger-11",
};

function kindOf(node: FlowNode): string {
  return node.kind === "outcome" ? (node.outcome ?? "continue") : node.kind;
}

function box(at: { x: number; y: number; w: number; h: number }): React.CSSProperties {
  return { position: "absolute", left: at.x, top: at.y, width: at.w, minHeight: at.h };
}

export function StartCard({ at, onAdd }: { at: PlacedNode; onAdd?: () => void }) {
  return (
    <div style={box(at)} data-card className="rounded-xl border border-dashed border-border bg-surface/80 p-3">
      <div className="flex items-center gap-2">
        <span className="grid size-6 place-items-center rounded-md bg-gray-3 text-text-muted">
          <Play className="size-3.5" />
        </span>
        <span className="text-sm font-medium">The intake starts here</span>
      </div>
      {onAdd ? (
        <Button variant="outline" size="sm" className="mt-2 w-full" onClick={onAdd}>
          <Plus className="mr-1 size-3.5" />
          Ask the first question
        </Button>
      ) : null}
    </div>
  );
}

/**
 * A path with nothing on it yet.
 *
 * Every path begins like this the moment "make a path per answer" creates one, so this card is
 * what tells an admin the path is really there and where to build on it. Drawing nothing is what
 * made four new paths look like none.
 */
export function GhostCard({
  at,
  readOnly,
  onAdd,
}: {
  at: PlacedNode;
  readOnly: boolean;
  onAdd: () => void;
}) {
  return (
    <div
      style={box(at)}
      data-card
      className="grid place-items-center rounded-xl border border-dashed border-border bg-surface-sunken/40 p-3 text-center"
    >
      <div>
        <p className="text-xs text-text-muted">Nothing is asked on this path yet.</p>
        {readOnly ? null : (
          <Button variant="ghost" size="sm" className="mt-1" onClick={onAdd}>
            <Plus className="mr-1 size-3.5" />
            Add a step
          </Button>
        )}
      </div>
    </div>
  );
}

export function NodeCard({
  at,
  node,
  subtitle,
  index,
  invalid,
  readOnly,
  branchBlocked,
  onEdit,
  onDelete,
  onAddAfter,
  afterBlocked,
  onAddBefore,
  beforeBlocked,
  onAddBranch,
  onBranchOnAnswer,
  onVaryBy,
  varied = false,
  onAddCatchAll,
}: {
  at: PlacedNode;
  node: FlowNode;
  subtitle: string | null;
  index: number;
  invalid: boolean;
  readOnly: boolean;
  /** Why branching on the answer is unavailable, or null when it is. */
  branchBlocked: string | null;
  onEdit: () => void;
  onDelete: () => void;
  onAddAfter: () => void;
  /** Why a step cannot go after this one, or null when it can. */
  afterBlocked: string | null;
  onAddBefore: () => void;
  /** Why a step cannot go above this one, or null when it can. */
  beforeBlocked: string | null;
  onAddBranch: () => void;
  onBranchOnAnswer: () => void;
  onVaryBy: () => void;
  /** This Paths step already varies by a firm or a campaign, so Vary by edits that pick. */
  varied?: boolean;
  /** Add a last branch with no conditions, when the step has none and the admin wants one. */
  onAddCatchAll: (() => void) | null;
}) {
  const kind = kindOf(node);
  const Icon = ICONS[kind] ?? MessageCircleQuestion;
  return (
    <article
      style={box(at)}
      data-card
      role="treeitem"
      aria-level={at.depth + 1}
      aria-label={node.title}
      className={cn(
        "group rounded-xl border bg-surface p-3 shadow-sm transition-shadow hover:shadow-md",
        invalid ? "border-danger-4" : "border-border",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span
          className={cn(
            "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium",
            BADGE[kind] ?? BADGE.question,
          )}
        >
          <Icon className="size-3" />
          {kind === "paths" ? "Paths" : kind === "question" ? "Question" : "End"}
        </span>
        {readOnly ? null : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={`What to do with ${node.title}`}
                className="rounded p-0.5 text-text-muted opacity-0 transition-opacity hover:bg-surface-hover focus-visible:opacity-100 group-hover:opacity-100"
              >
                <MoreHorizontal className="size-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={onEdit}>Edit</DropdownMenuItem>
              <DropdownMenuItem
                disabled={Boolean(afterBlocked)}
                title={afterBlocked ?? undefined}
                onSelect={() => !afterBlocked && onAddAfter()}
              >
                Add a step after
              </DropdownMenuItem>
              {/* Disabled with the reason, never hidden, as the other conditional actions are. */}
              <DropdownMenuItem
                disabled={Boolean(beforeBlocked)}
                title={beforeBlocked ?? undefined}
                onSelect={() => !beforeBlocked && onAddBefore()}
              >
                Add a step before
              </DropdownMenuItem>
              {node.kind === "question" ? (
                // Disabled with the reason, never hidden: an action that simply vanishes is what
                // sent the admin looking for it.
                <DropdownMenuItem
                  disabled={Boolean(branchBlocked)}
                  title={branchBlocked ?? undefined}
                  onSelect={() => !branchBlocked && onBranchOnAnswer()}
                >
                  Make a path per answer
                </DropdownMenuItem>
              ) : null}
              {node.kind === "paths" ? (
                <>
                  <DropdownMenuItem onSelect={onAddBranch}>Add a branch</DropdownMenuItem>
                  {/* Named for what it does here: on a step that already fans out it edits the
                      pick, and "Vary by" read as if it would start again. */}
                  <DropdownMenuItem onSelect={onVaryBy}>
                    {varied ? "Which firms or campaigns" : "Vary by firm or campaign"}
                  </DropdownMenuItem>
                </>
              ) : null}
              {onAddCatchAll ? (
                <DropdownMenuItem onSelect={onAddCatchAll}>
                  Add a path for everyone else
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem onSelect={onDelete}>Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <p className="mt-1.5 text-sm font-medium leading-snug">
        <span className="text-text-muted">{index}. </span>
        {node.title}
      </p>
      {subtitle ? <p className="mt-0.5 truncate text-xs text-text-muted">{subtitle}</p> : null}
    </article>
  );
}

/** The named head of a branch, as the reference draws it: a pill on the bus. */
export function BranchPill({
  at,
  sentence,
  readOnly,
  removable,
  onEdit,
  onAddHere,
  onRemove,
}: {
  at: PlacedPill;
  /** What the branch asks, in words, for the admin and for a screen reader. */
  sentence: string;
  readOnly: boolean;
  removable: boolean;
  onEdit: () => void;
  /** Put a step on this one path, which is how a split whose paths diverge gains one. */
  onAddHere: () => void;
  onRemove: () => void;
}) {
  return (
    <div style={box(at)} data-card className="flex items-center justify-center">
      <div
        className={cn(
          "inline-flex max-w-full items-center gap-1 rounded-full border px-2.5 py-1",
          at.fallback
            ? "border-dashed border-border bg-surface-sunken/60 text-text-muted"
            : "border-accent-4 bg-accent-2 text-accent-11",
        )}
      >
        <span className="truncate text-xs font-medium" title={sentence}>
          {at.label ?? (at.fallback ? "Everyone else" : "Branch")}
        </span>
        {readOnly ? null : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={`What to do with the ${at.label ?? "fallback"} path`}
                className="rounded p-0.5 hover:bg-surface-hover"
              >
                <MoreHorizontal className="size-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onSelect={onEdit}>
                {at.fallback ? "Rename this path" : "Who takes this path"}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={onAddHere}>Add a step on this path</DropdownMenuItem>
              <DropdownMenuItem
                disabled={!removable}
                title={removable ? undefined : "The last path is the base flow and stays."}
                onSelect={() => removable && onRemove()}
              >
                Remove this path
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <span className="sr-only">{sentence}</span>
    </div>
  );
}
